import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, switchMap, throwError } from 'rxjs';
import { getSubdomainPreview, validateSubdomainFormat } from 'src/app/_helpers/utils.index';
import { BusinessSetupProgressService } from 'src/app/_services/business-setup-progress.service';
import { BusinessSetupService, OnlineStoreCreatedPayload } from 'src/app/_services/business-setup.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { GlobalService } from 'src/app/_services/global.service';
import { SignupService } from 'src/app/_services/signup.service';
import { UserService } from 'src/app/_services/user.service';
import { environment } from 'src/environments/environment';

interface StoreTemplateOption {
  key: string;
  name: string;
  description: string;
  image: string;
  templateID: number;
  demoUrl: string;
  category?: string;
  primaryColor?: string;
  previewImages?: string[];
}

interface BrandColorOption {
  name: string;
  code: string;
  hex: string;
}

@Component({
  selector: 'app-online-store-setup',
  templateUrl: './online-store-setup.component.html',
  styleUrls: ['./online-store-setup.component.scss']
})
export class OnlineStoreSetupComponent implements OnInit {
  _global = GlobalService;
  readonly minSubdomainLength = 3;
  // Same allow-list/behavior as theme-overview.component.ts's isYouksStoreUser(): these accounts'
  // production stores have no auth_token-based editor entry point, just the plain store link.
  private readonly youksStoreEmailAllowList = new Set([
    'zilonwebmaster@gmail.com',
    'zilonwebsmaster@gmail.com',
    'younksnutritioncentral@yahoo.com'
  ]);
  readonly wordPressStoreTemplates: StoreTemplateOption[] = [
    {
      key: 'timelore',
      name: 'Timelore',
      description: 'Dark, high-contrast hero. Built for watches, tech and accessories.',
      image: 'assets/images/store-templates/tpl-timelore.webp',
      templateID: 3,
      demoUrl: 'https://timelore.lincsell.com/'
    },
    {
      key: 'luminest',
      name: 'Luminest',
      description: 'Warm and airy editorial look for home, decor and lifestyle.',
      image: 'assets/images/store-templates/tpl-luminest.webp',
      templateID: 2,
      demoUrl: 'https://luminestdemo.lincsell.com/'
    },
    {
      key: 'trendovo',
      name: 'Trendovo',
      description: 'Clean greyscale, fashion-first product grid.',
      image: 'assets/images/store-templates/tpl-trendovo.webp',
      templateID: 1,
      demoUrl: 'https://trendovodemo.lincsell.com/'
    }
  ];

  // Mirrors business-setup-checklist.component.ts's aiEditorStoreTemplates. Starts as this single
  // 'classic' fallback entry and is replaced by fetchAiEditorTemplates() once the real catalog
  // (GET online-store/templates) loads — kept as-is so the picker still shows something usable if
  // that request hasn't resolved yet or fails. createAiEditorStore() sends the selected entry's
  // key (== template.id from the catalog, e.g. "modern-boutique-a1b2c3") as templateId, falling
  // back to 'classic' only if nothing is selected yet.
  aiEditorStoreTemplates: StoreTemplateOption[] = [
    {
      key: 'classic',
      name: 'Classic',
      description: 'The AI editor\'s default starting theme — fully customizable after your store is created.',
      image: 'assets/images/store-templates/tpl-classic.svg',
      templateID: 0,
      demoUrl: ''
    }
  ];
  isLoadingAiEditorTemplates = false;

  /** Synchronous by necessity (drives an *ngFor) — reads isAiEditorStore directly, same as
   *  business-setup-checklist.component.ts's storeTemplates getter. By the time a merchant opens
   *  this dialog, loadActiveEditor() (called in ngOnInit) has had time to resolve. */
  get storeTemplates(): StoreTemplateOption[] {
    return this.isAiEditorStore ? this.aiEditorStoreTemplates : this.wordPressStoreTemplates;
  }
  readonly brandColorOptions: BrandColorOption[] = [
    { name: 'Blue', code: 'blue', hex: '#2563EB' },
    { name: 'Navy', code: 'navy', hex: '#1E3A8A' },
    { name: 'Teal', code: 'teal', hex: '#0D9488' },
    { name: 'Cyan', code: 'cyan', hex: '#06B6D4' },
    { name: 'Sky Blue', code: 'sky_blue', hex: '#0EA5E9' },
    { name: 'Royal Blue', code: 'royal_blue', hex: '#1D4ED8' },
    { name: 'Indigo', code: 'indigo', hex: '#4F46E5' },
    { name: 'Violet', code: 'violet', hex: '#7C3AED' },
    { name: 'Orange', code: 'orange', hex: '#F97316' },
    { name: 'Amber', code: 'amber', hex: '#F59E0B' },
    { name: 'Yellow', code: 'yellow', hex: '#EAB308' },
    { name: 'Gold', code: 'gold', hex: '#CA8A04' },
    { name: 'Green', code: 'green', hex: '#22C55E' },
    { name: 'Emerald', code: 'emerald', hex: '#10B981' },
    { name: 'Lime', code: 'lime', hex: '#84CC16' },
    { name: 'Purple', code: 'purple', hex: '#9333EA' },
    { name: 'Magenta', code: 'magenta', hex: '#D946EF' },
    { name: 'Pink', code: 'pink', hex: '#EC4899' },
    { name: 'Red', code: 'red', hex: '#EF4444' },
    { name: 'Coral', code: 'coral', hex: '#FB7185' },
    { name: 'Brown', code: 'brown', hex: '#8B5E3C' },
    { name: 'White', code: 'white', hex: '#FFFFFF' },
    { name: 'Gray', code: 'gray', hex: '#6B7280' },
    { name: 'Black', code: 'black', hex: '#111827' }
  ];
  subdomain = '';
  selectedBrandColors: string[] = [];
  selectedTemplateKey = '';
  pendingTemplateKey = '';
  domainAvailable: boolean | null = null;
  domainUnavailableReason: string | null = null;
  isCheckingDomainAvailability = false;
  isSavingStore = false;
  isTemplateDialogVisible = false;
  customStoreRequestVisible = false;
  isSubmittingCustomStoreRequest = false;
  isOpeningStoreEditor = false;
  termsAccepted = false;
  isTermsDialogVisible = false;
  private customStoreRequestSubmitted = false;
  private storeJustCreated = false;

  constructor(
    private router: Router,
    private businessSetupService: BusinessSetupService,
    private userService: UserService,
    private connMessageService: ConnMessageService,
    private businessSetupProgressService: BusinessSetupProgressService,
    private signupService: SignupService
  ) {}

  ngOnInit(): void {
    this.loadActiveEditor();
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    if (!setup) return;

    // brandColorSelections is often present but empty ([]) once brandColors itself has the real
    // saved values — `??` only falls through on null/undefined, not on a defined-but-empty array,
    // so each candidate must be checked for actual content, not just presence.
    const brandColorCandidates = [
      setup.brandColorSelections,
      setup.BrandColorSelections,
      setup.brandColors,
      setup.BrandColors,
      setup.brandColorDetails,
      setup.BrandColorDetails
    ];
    const savedBrandColors = brandColorCandidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0) ?? [];
    this.selectedBrandColors = Array.isArray(savedBrandColors)
      ? savedBrandColors
        .map((color: any) => typeof color === 'string' ? color : (color?.code ?? color?.Code))
        .map((color: any) => String(color).trim().toLowerCase())
        .filter((color: any) => !!this.getBrandColorOption(color))
      : [];

    if (this.isCustomStorePending) {
      // Prefer the server-persisted values (survive across browsers/devices) over the
      // localStorage fallback below, which only helps on the same browser that submitted the
      // request.
      if (this.onlineStoreUrl) {
        this.subdomain = this.resolveSavedSubdomain(this.onlineStoreUrl);
      }
      this.selectedTemplateKey = this.resolveSavedTemplateKey(setup) || this.readCachedCreatedTemplateKey();

      this.restoreCustomStoreRequestDetails();
      this.domainAvailable = true;
    }

    if (this.isStoreCreated) {
      this.subdomain = this.resolveSavedSubdomain(this.onlineStoreUrl);
      // Some accounts finished store creation before onlineStoreTemplateId was persisted
      // server-side (or the backend GET response simply omits it), leaving no way to tell which
      // template the merchant actually picked. Fall back to the locally cached choice from the
      // moment of creation so the template card doesn't just disappear on a fresh page load.
      this.selectedTemplateKey = this.resolveSavedTemplateKey(setup) || this.readCachedCreatedTemplateKey();
      this.domainAvailable = true;
    }

    if (!this.isCustomStorePending && !this.isStoreCreated) {
      const businessName = String(setup.businessDisplayName ?? setup.BusinessDisplayName ?? setup.businessName ?? setup.BusinessName ?? '').trim();
      this.applyAutoGeneratedSubdomain(businessName);
    }
  }

  /** Mirrors business-setup-checklist.component.ts's applyAutoGeneratedSubdomain(): prefill the
   *  subdomain field from the saved business name (like the "timelore" placeholder becoming a real
   *  suggestion) and kick off the availability check immediately, instead of leaving the field
   *  blank until the merchant types something. */
  private applyAutoGeneratedSubdomain(businessName: string): void {
    if (!businessName || this.subdomain.trim()) return;

    this.subdomain = getSubdomainPreview(businessName);
    this.onSubdomainBlur();
  }

  /** Searches both catalogs, not just the current isAiEditorStore-gated storeTemplates list — the
   *  saved selectedTemplateKey may belong to whichever catalog was active when this store's
   *  template was actually created, which can differ from today's account-wide active-editor
   *  setting (see resolveSavedTemplateKey() below for the same reasoning). Key collisions between
   *  the two catalogs never happen (WordPress keys are template slugs like 'timelore'; AI-editor
   *  keys are opaque catalog ids or 'classic'), so checking both is always safe. */
  get selectedTemplate(): StoreTemplateOption | undefined {
    // Check the account's currently active catalog first — an AI-editor catalog id can collide
    // with a hardcoded WordPress key (e.g. both using "timelore"), and checking wordPressStoreTemplates
    // unconditionally first would resolve to the wrong (WordPress) entry/image for such accounts.
    return this.storeTemplates.find((template) => template.key === this.selectedTemplateKey)
      ?? this.wordPressStoreTemplates.find((template) => template.key === this.selectedTemplateKey)
      ?? this.aiEditorStoreTemplates.find((template) => template.key === this.selectedTemplateKey);
  }

  get subdomainTooShort(): boolean {
    const trimmed = this.subdomain.trim();
    return trimmed.length > 0 && trimmed.length < this.minSubdomainLength;
  }

  get onlineStoreUrl(): string {
    return String(
      GlobalService.userInfo?.businessSetup?.[0]?.onlineStoreUrl ??
      GlobalService.userInfo?.businessSetup?.[0]?.OnlineStoreUrl ??
      ''
    ).trim();
  }

  get isStoreCreated(): boolean {
    if (this.storeJustCreated) return true;
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    return !!(setup?.onlineStoreCreated ?? setup?.OnlineStoreCreated);
  }

  /** Template customization (theme-overview editor) is a separate step from store creation —
   *  TemplateSetupStartedDate gets set as soon as the site is created, but the backend has no
   *  endpoint that ever writes TemplateSetupCompleted/TemplateSetupCompletedDate (confirmed against
   *  BusinessSetupFlags.cs/OnlineStoreProvisioning.cs — those flags are read-only dead fields today),
   *  so gating purely on them would leave Preview/Edit permanently disabled for every merchant.
   *  OnlineStoreSetupStatus IS writable today (via the admin-only online-store-created call's
   *  Status param), so treat "Completed" there as the interim signal until a real
   *  TemplateSetupCompleted-setting endpoint exists on the backend. */
  get isTemplateSetupCompleted(): boolean {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    if (setup?.templateSetupCompleted ?? setup?.TemplateSetupCompleted) return true;
    const status = String(setup?.onlineStoreSetupStatus ?? setup?.OnlineStoreSetupStatus ?? '').trim().toLowerCase();
    return status === 'completed';
  }

  get isCustomStorePending(): boolean {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const requested =
      setup?.customOnlineStoreRequested ?? setup?.CustomOnlineStoreRequested
      ?? setup?.availSaveOffer ?? setup?.AvailSaveOffer;
    return !this.isStoreCreated && (
      this.customStoreRequestSubmitted
      || requested === true
      || String(requested).toLowerCase() === 'true'
      || this.businessSetupProgressService.hasRequestedCustomOnlineStore
    );
  }

  get showStoreStatus(): boolean {
    return this.isStoreCreated || this.isCustomStorePending;
  }

  /** Single badge summarizing where this merchant's store actually stands, driven off the same
   *  backend flags used to gate Preview/Edit above rather than re-deriving separate conditions. */
  get storeStatusLabel(): string {
    if (this.isCustomStorePending) return 'Custom build in progress';
    if (!this.isStoreCreated) return 'Setup required';
    if (this.isTemplateSetupCompleted) return 'Live';
    return 'Setup in progress';
  }

  get storeStatusClass(): string {
    if (this.isCustomStorePending) return 'store-status-pending';
    if (!this.isStoreCreated) return 'store-status-required';
    if (this.isTemplateSetupCompleted) return 'store-status-live';
    return 'store-status-pending';
  }

  /** Gates the whole store-setup form behind Business information being filled in first — mirrors
   *  BusinessSetupProgressService.isBusinessInfoComplete, read live off GlobalService.userInfo so
   *  the lock clears itself the moment business info is saved elsewhere, without a page reload. */
  get isBusinessInfoComplete(): boolean {
    return this.businessSetupProgressService.isBusinessInfoComplete;
  }

  goToBusinessInfo(): void {
    this.router.navigate(['/app/business-setup'], { queryParams: { from: 'online-store-setup' } });
  }

  /** Custom-build requests only make sense for the WordPress path today — the AI editor has no
   *  "contact us for a custom build" fulfillment behind it, so hide that option once the account's
   *  active editor resolves to AI (mirrors the "ai" substring check used everywhere else here). */
  get isAiEditorStore(): boolean {
    return String(GlobalService.activeEditor ?? '').trim().toLowerCase().includes('ai');
  }

  get isYouksStoreUser(): boolean {
    const email = String(GlobalService.userInfo?.email ?? GlobalService.userInfo?.username ?? '').trim().toLowerCase();
    return this.youksStoreEmailAllowList.has(email);
  }

  /** True while either "Create store" or "Contact us for a custom online store" is in flight —
   *  they're mutually exclusive actions on the same form, so the other button/inputs must lock
   *  too or a merchant can fire both at once against the same pending subdomain/template. */
  get isBusy(): boolean {
    return this.isSavingStore || this.isSubmittingCustomStoreRequest;
  }

  get canPreviewStore(): boolean {
    return this.isStoreCreated && !!this.onlineStoreUrl;
  }

  /** The store editor isn't usable on phones, so Edit is blocked there (Preview stays available). */
  get isMobileDevice(): boolean {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  get canEditStore(): boolean {
    return this.isStoreCreated && !!this.onlineStoreUrl && !this.isMobileDevice;
  }

  get pendingTemplate(): StoreTemplateOption | undefined {
    return this.storeTemplates.find((template) => template.key === this.pendingTemplateKey);
  }

  get canCreateStore(): boolean {
    return !!(
      this.subdomain.trim() &&
      !this.subdomainTooShort &&
      this.domainAvailable === true &&
      this.selectedBrandColors.length > 0 &&
      this.selectedTemplateKey &&
      this.termsAccepted &&
      !this.isBusy &&
      !this.isCheckingDomainAvailability
    );
  }

  get canRequestCustomStore(): boolean {
    return !!(
      !this.isStoreCreated &&
      this.subdomain.trim() &&
      !this.subdomainTooShort &&
      this.domainAvailable === true &&
      this.selectedBrandColors.length > 0 &&
      this.selectedTemplateKey &&
      this.termsAccepted &&
      !this.isCheckingDomainAvailability &&
      !this.isBusy
    );
  }

  openTermsDialog(event: Event): void {
    event.preventDefault();
    this.isTermsDialogVisible = true;
  }

  closeTermsDialog(): void {
    this.isTermsDialogVisible = false;
  }

  get customStoreContactEmail(): string {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    return String(
      setup?.businessEmail ?? setup?.BusinessEmail ?? GlobalService.userInfo?.email ?? GlobalService.userInfo?.username ?? ''
    ).trim();
  }

  getBrandColorOption(code: string): BrandColorOption | undefined {
    return this.brandColorOptions.find((color) => color.code === code);
  }

  getBrandColorTextColor(code: string): string {
    const hex = this.getBrandColorOption(code)?.hex?.replace('#', '');
    if (!hex || hex.length !== 6) return '#101828';

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
    return luminance > 160 ? '#101828' : '#ffffff';
  }

  removeSelectedBrandColor(code: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedBrandColors = this.selectedBrandColors.filter((selectedCode) => selectedCode !== code);
  }

  onSubdomainInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.subdomain = validateSubdomainFormat(input.value);
    input.value = this.subdomain;
    this.domainAvailable = null;
    this.domainUnavailableReason = null;
  }

  onSubdomainBlur(): void {
    if (this.isStoreCreated) return;

    this.subdomain = validateSubdomainFormat(this.subdomain.trim());
    if (!this.subdomain || this.subdomainTooShort) {
      this.domainAvailable = null;
      return;
    }

    this.isCheckingDomainAvailability = true;
    this.domainUnavailableReason = null;
    this.checkSubdomainAvailability(this.subdomain).subscribe({
      next: (available) => {
        this.domainAvailable = available;
      },
      error: (err) => {
        this.domainAvailable = false;
        this.domainUnavailableReason = err?.error?.message || null;
        this.isCheckingDomainAvailability = false;
      },
      complete: () => {
        this.isCheckingDomainAvailability = false;
      }
    });
  }

  /** WordPress accounts check the legacy multisite directly (SignupService.CheckSubdomainExists)
   *  — the only endpoint that knows about every WordPress-claimed subdomain. AI-editor accounts
   *  check editor.lincsell.com's own check-domain instead (BusinessSetupService.
   *  checkDomainAvailability → auth-svc's online-store/check-domain, confirmed as a genuine
   *  pass-through to the site builder's check-domain, one editor per subdomain namespace so this
   *  is the correct check for that path). Goes through getActiveEditorCached() rather than the
   *  synchronous isWordPressStore() so a check fired right after page load doesn't race
   *  loadActiveEditor()'s fetch and silently fall back to WordPress before it resolves. */
  private checkSubdomainAvailability(domain: string): Observable<boolean> {
    return this.businessSetupService.getActiveEditorCached().pipe(
      switchMap((editor) => {
        if (String(editor).trim().toLowerCase().includes('ai')) {
          return this.businessSetupService.checkDomainAvailability(domain).pipe(
            map((res: any) => res?.success === true && !!(res.data?.available ?? res.available))
          );
        }

        return this.signupService.CheckSubdomainExists({ business_name: domain }).pipe(
          map((res: any) => res?.data?.message === 'AVAILABLE')
        );
      })
    );
  }

  selectTemplate(key: string): void {
    this.pendingTemplateKey = key;
  }

  openTemplateDialog(): void {
    this.pendingTemplateKey = this.selectedTemplateKey;
    this.isTemplateDialogVisible = true;
  }

  confirmTemplate(): void {
    if (!this.pendingTemplateKey) return;

    this.selectedTemplateKey = this.pendingTemplateKey;
    this.isTemplateDialogVisible = false;
  }

  previewTemplate(template: StoreTemplateOption, event: Event): void {
    event.stopPropagation();
    window.open(template.demoUrl, '_blank', 'noopener');
  }

  /** Once a store is created, its onlineStoreUrl IS the live published domain (e.g.
   *  "you241.lincsell.com") for both WordPress and AI-editor stores — Preview just opens it
   *  directly instead of rebuilding the old editor.lincsell.com/storefront/{subdomain} link. */
  previewOnlineStore(): void {
    if (!this.onlineStoreUrl) return;
    const url = /^https?:\/\//i.test(this.onlineStoreUrl) ? this.onlineStoreUrl : `https://${this.onlineStoreUrl}`;
    window.open(url, '_blank', 'noopener');
  }

  goToStoreEditor(): void {
    if (!this.onlineStoreUrl || this.isMobileDevice) return;
    const url = /^https?:\/\//i.test(this.onlineStoreUrl) ? this.onlineStoreUrl : `https://${this.onlineStoreUrl}`;

    if (this.isYouksStoreUser) {
      window.open(url, '_blank', 'noopener');
      return;
    }

    // Open the tab synchronously, inside the click handler, so browsers don't block it as a
    // popup once the async getActiveEditorCached()/exchange resolves — same reasoning as
    // openAiStoreEditor() below. of(cached) resolves synchronously when the editor flag is
    // already warm, so the common case still navigates within this same call stack.
    const editorTab = window.open('', '_blank');
    this.businessSetupService.getActiveEditorCached().subscribe((editor) => {
      if (!String(editor).trim().toLowerCase().includes('ai')) {
        const wpUrl = `${url}?auth_token=signmein`;
        if (editorTab) {
          editorTab.location.href = wpUrl;
        } else {
          window.open(wpUrl, '_blank', 'noopener');
        }
        return;
      }

      this.openAiStoreEditor(editorTab);
    });
  }

  /** Race-free replacement for a synchronous "is WordPress?" read: goes through
   *  getActiveEditorCached() (of(cached) once GlobalService.activeEditor is warm) instead of
   *  reading GlobalService.activeEditor directly, so a call fired right after page load doesn't
   *  race loadActiveEditor()'s own fetch and silently default to WordPress before it resolves. */
  private isWordPressStore$(): Observable<boolean> {
    return this.businessSetupService.getActiveEditorCached().pipe(
      map((editor) => !String(editor).trim().toLowerCase().includes('ai'))
    );
  }

  private loadActiveEditor(): void {
    this.businessSetupService.getActiveEditorCached().subscribe((editor) => {
      // The full catalog (GET online-store/templates) is only needed to populate the "choose/
      // change template" picker — once the store is created (or a custom-build request is already
      // pending), that picker is never shown again (see the *ngIf="!showStoreStatus" change-
      // template button in the .html), and the saved template's own display data now comes
      // straight off the business profile via resolveSavedAiTemplateFromProfile(), so re-fetching
      // the whole catalog on every load of an already-completed store is pure waste.
      if (String(editor).trim().toLowerCase().includes('ai') && !this.showStoreStatus) {
        this.fetchAiEditorTemplates();
      }
    });
  }

  /** Loads the AI editor's real template catalog (GET online-store/templates) to replace the
   *  single hardcoded 'classic' fallback in aiEditorStoreTemplates above. On failure, or while it's
   *  still loading, the fallback entry stays in place so the picker remains usable. If the
   *  in-progress/saved selection no longer exists in the freshly loaded catalog (e.g. it pointed at
   *  the fallback 'classic' key before the real catalog arrived), it's cleared so a merchant isn't
   *  left with a phantom selection instead of being asked to pick again. */
  private fetchAiEditorTemplates(): void {
    const username = String(GlobalService.userInfo?.email ?? GlobalService.userInfo?.username ?? '');
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const businessIdentifier = String(setup?.businessNameWithDomain ?? '');
    if (!username || !businessIdentifier) return;

    this.isLoadingAiEditorTemplates = true;
    this.businessSetupService.getOnlineStoreTemplates(username, businessIdentifier).pipe(
      finalize(() => {
        this.isLoadingAiEditorTemplates = false;
        // Runs after every path above (a match, no match, an empty/failed response, or a network
        // error). Re-runs the full WordPress-then-AI resolveSavedTemplateKey() lookup here too,
        // rather than assuming "still empty" means the ngOnInit hydration already ruled out a
        // WordPress match — this HTTP call and that hydration's own async work race each other
        // with no guaranteed order, so this finalize() can complete first and needs to be able to
        // resolve a WordPress-catalog selection (e.g. 'timelore') on its own. Only once BOTH
        // catalogs come up empty does it fall back to the first available AI template so a
        // created/pending store always shows *something* instead of a blank card.
        if (!this.selectedTemplateKey && this.showStoreStatus) {
          const setup = GlobalService.userInfo?.businessSetup?.[0];
          this.selectedTemplateKey = (setup && this.resolveSavedTemplateKey(setup))
            || (this.aiEditorStoreTemplates.length ? this.aiEditorStoreTemplates[0].key : '');
        }
      })
    ).subscribe({
      next: (res: any) => {
        const templates = res?.data?.templates;
        if (res?.success !== true || !Array.isArray(templates) || templates.length === 0) return;

        // Capture the currently-resolved selection (e.g. the legacy 'classic' fallback entry a
        // store created before this real catalog existed is still pointing at) before it's
        // overwritten below — the account's stored onlineStoreTemplateId has no counterpart in the
        // real catalog, so without this the card just disappears instead of continuing to show
        // what the merchant actually has.
        const previouslySelectedTemplate = this.selectedTemplate;

        this.aiEditorStoreTemplates = templates.map((template: any) => ({
          key: String(template.id),
          name: String(template.label ?? template.id ?? ''),
          description: String(template.description ?? ''),
          image: template.thumbnailUrl ? encodeURI(String(template.thumbnailUrl)) : 'assets/images/store-templates/tpl-classic.svg',
          templateID: 0,
          demoUrl: String(template.demoUrl ?? ''),
          category: String(template.category ?? ''),
          primaryColor: String(template.primaryColor ?? ''),
          previewImages: Array.isArray(template.previewImages) ? template.previewImages.map(String) : []
        }));
        // Backend catalog thumbnailUrls can 404 (e.g. a missing/mis-cased blob) — a broken
        // background-image URL doesn't clear the div's previous paint, so a merchant switching
        // templates keeps seeing the last template's photo under the new template's label. Probe
        // each thumbnail and swap in the local fallback asset before it's ever bound to the DOM.
        this.aiEditorStoreTemplates.forEach((template) => this.verifyTemplateImage(template));

        // A WordPress-catalog selection (e.g. 'timelore', resolved by resolveSavedTemplateKey()
        // against the store's real numeric templateID) will never appear in this AI catalog — this
        // method only concerns the AI catalog, so it must never touch/clear a selection that
        // already belongs to WordPress instead.
        const isWordPressSelection = this.wordPressStoreTemplates.some((template) => template.key === this.selectedTemplateKey);

        if (this.pendingTemplateKey && !this.aiEditorStoreTemplates.some((template) => template.key === this.pendingTemplateKey)) {
          this.pendingTemplateKey = '';
        }
        if (!isWordPressSelection && this.selectedTemplateKey && !this.aiEditorStoreTemplates.some((template) => template.key === this.selectedTemplateKey)) {
          if (previouslySelectedTemplate && previouslySelectedTemplate.key === this.selectedTemplateKey) {
            this.aiEditorStoreTemplates = [...this.aiEditorStoreTemplates, previouslySelectedTemplate];
          } else {
            this.selectedTemplateKey = '';
          }
        }
        // ngOnInit's initial resolveSavedTemplateKey() call ran before this real catalog existed
        // (only the single 'classic' fallback was available then), so a created/pending AI-editor
        // store's saved template id had nothing to match against. Retry now that the real catalog
        // is here.
        if (!this.selectedTemplateKey && this.showStoreStatus) {
          const setup = GlobalService.userInfo?.businessSetup?.[0];
          this.selectedTemplateKey = (setup && this.resolveSavedTemplateKey(setup)) || this.readCachedCreatedTemplateKey();
        }
      },
      error: () => {
        // Keep the existing (fallback Classic) entry — the picker still functions with one option.
      }
    });
  }

  /** Preloads a template's thumbnail and swaps in the local fallback asset if it 404s (mutates
   *  the shared StoreTemplateOption object in place, so both the modal grid and the selected-
   *  template preview — which read the same array entry — pick up the corrected image). */
  private verifyTemplateImage(template: StoreTemplateOption): void {
    if (!template.image || template.image.startsWith('assets/')) return;
    const probe = new Image();
    probe.onerror = () => {
      template.image = 'assets/images/store-templates/tpl-classic.svg';
    };
    probe.src = template.image;
  }

  /** Mirrors theme-overview.component.ts's openStoreEditor() AI-editor branch: exchange for an
   *  auth code (the AI-editor API implemented in "ai editor init") and hand the pre-opened tab
   *  off to the editor's callback URL. Reuses the tab the caller already opened synchronously
   *  (see goToStoreEditor()) instead of opening its own, so there's only ever one popup. */
  private openAiStoreEditor(editorTab: Window | null): void {
    this.isOpeningStoreEditor = true;
    const username = String(GlobalService.userInfo?.email ?? GlobalService.userInfo?.username ?? '');
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const businessIdentifier = String(setup?.businessNameWithDomain ?? '');

    this.businessSetupService.getFreshStoreEditorAuthCode(username, businessIdentifier).pipe(
      finalize(() => {
        this.isOpeningStoreEditor = false;
      })
    ).subscribe({
      next: (res: any) => {
        const code = res?.data?.code;
        if (res?.success === false || !code) {
          editorTab?.close();
          this.connMessageService.showToastMessage('Unable to open the online store editor. Please try again.', 'error');
          return;
        }

        const callbackUrl = `${environment.StoreEditorBaseURL}auth/callback?code=${encodeURIComponent(code)}`;
        if (editorTab) {
          editorTab.location.href = callbackUrl;
        } else {
          window.open(callbackUrl, '_blank');
        }
      },
      error: () => {
        editorTab?.close();
        this.connMessageService.showToastMessage('Unable to open the online store editor. Please try again.', 'error');
      }
    });
  }

  requestCustomOnlineStore(): void {
    if (!this.canRequestCustomStore) return;

    const userId = GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId;
    const userEmail = GlobalService.userInfo?.email ?? GlobalService.userInfo?.username;
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const businessId = setup?.businessId ?? setup?.id;
    if (!userId || !userEmail || !businessId) {
      this.connMessageService.showToastMessage('Unable to resolve your account. Please refresh and try again.', 'error');
      return;
    }

    this.isSubmittingCustomStoreRequest = true;
    const trimmedSubdomain = validateSubdomainFormat(this.subdomain.trim());
    const brandColorNames = this.selectedBrandColors
      .map((code) => this.getBrandColorOption(code)?.name)
      .filter((name): name is string => !!name);

    this.businessSetupService.requestCustomOnlineStore({
      userId,
      businessId,
      userEmail,
      subdomain: trimmedSubdomain,
      brandColors: brandColorNames,
      onlineStoreTemplateId: this.selectedTemplate?.templateID,
      templateName: this.selectedTemplate?.name,
      templateImageUrl: this.selectedTemplate?.image
    }).pipe(
      // The request response itself doesn't carry the full businessSetup record back — refetch
      // via B2CGraphClientApi (GETUSERBYUSERNAME) so GlobalService.userInfo picks up the
      // server-persisted subdomain/brandColors/onlineStoreTemplateId/onlineStoreUrl immediately,
      // instead of only showing correctly after a hard reload re-bootstraps the session.
      switchMap(() => userEmail
        ? this.userService.getUserByUserName(userEmail).pipe(catchError(() => of(null)))
        : of(null)
      )
    ).subscribe({
      next: (userRes: any) => {
        if (userRes?.success && userRes?.data) {
          GlobalService.userInfo = userRes.data;
        }

        this.customStoreRequestSubmitted = true;
        const setup = GlobalService.userInfo?.businessSetup?.[0];
        if (setup) {
          setup.customOnlineStoreRequested = true;
          setup.CustomOnlineStoreRequested = true;
          setup.availSaveOffer = true;
          setup.AvailSaveOffer = true;
        }
        this.businessSetupProgressService.markCustomOnlineStoreRequested();
        this.persistCustomStoreRequestDetails();
        this.customStoreRequestVisible = true;
      },
      error: (error: any) => {
        this.isSubmittingCustomStoreRequest = false;
        this.connMessageService.showToastMessage(
          error?.error?.message || 'Unable to submit your custom online store request. Please try again.',
          'error'
        );
      },
      complete: () => {
        this.isSubmittingCustomStoreRequest = false;
      }
    });
  }

  closeCustomStoreRequestConfirmation(): void {
    this.customStoreRequestVisible = false;
  }

  createStore(): void {
    const trimmedSubdomain = validateSubdomainFormat(this.subdomain.trim());
    if (!this.canCreateStore || !trimmedSubdomain || !this.selectedTemplate) return;

    const username = GlobalService.userInfo?.email ?? GlobalService.userInfo?.username;
    const userId = GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId;
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const businessId = setup?.businessId ?? setup?.id;
    if (!username || !userId || !businessId) {
      this.connMessageService.showToastMessage('Unable to resolve your account. Please refresh and try again.', 'error');
      return;
    }

    // Set the busy flag synchronously, before the async editor-flag branch below, so
    // canCreateStore's !isBusy check disables the button immediately — otherwise a second click
    // landing in the gap before isWordPressStore$() resolves starts its own independent
    // subscription, and both fire once the (shared, single-fetch) observable emits, sending
    // duplicate create-site requests.
    this.isSavingStore = true;

    this.isWordPressStore$().subscribe((isWordPress) => {
      if (!isWordPress) {
        this.createAiEditorStore(username, userId, businessId, trimmedSubdomain);
        return;
      }

      this.createWordPressStore(username, userId, businessId, setup, trimmedSubdomain);
    });
  }

  private createWordPressStore(
    username: string,
    userId: string,
    businessId: number | string,
    setup: any,
    trimmedSubdomain: string
  ): void {
    this.isSavingStore = true;
    const legacyBusinessId = setup?.businessNameWithDomain || businessId;
    const domainPayload = {
      email: username,
      business_name: trimmedSubdomain,
      business_id: legacyBusinessId
    };

    this.signupService.updatedomain(domainPayload).pipe(
      switchMap((domainResponse: any) => {
        if (domainResponse?.data?.message !== 'UPDATED') {
          return throwError(() => new Error('Failed to update domain. Please try again.'));
        }
        return this.saveThemeConfiguration(trimmedSubdomain);
      }),
      switchMap((themeResponse: any) => {
        if (themeResponse?.data !== true && themeResponse?.success !== true) {
          return throwError(() => new Error('Error saving store template!'));
        }
        return this.businessSetupService.createB2CSite(
          userId,
          businessId,
          username,
          trimmedSubdomain,
          String(this.selectedTemplate!.templateID)
        );
      }),
      switchMap((editorResponse: any) => {
        const siteUrl = this.resolveCreatedStoreUrl(editorResponse);
        if (editorResponse?.success !== true || !siteUrl) {
          return throwError(() => new Error(editorResponse?.message || 'Error creating your store site!'));
        }

        const completionPayload = this.buildOnlineStoreCreatedPayload(
          userId,
          businessId,
          trimmedSubdomain,
          siteUrl,
          editorResponse
        );
        return this.businessSetupService.markOnlineStoreCreated(completionPayload).pipe(
          map((completionResponse: any) => ({ completionResponse, siteUrl }))
        );
      }),
      map(({ completionResponse, siteUrl }) => {
        if (completionResponse?.success !== true) {
          throw new Error(completionResponse?.message || 'Unable to finish online store setup.');
        }
        return { completionResponse, siteUrl };
      }),
      finalize(() => {
        this.isSavingStore = false;
      })
    ).subscribe({
      next: ({ completionResponse, siteUrl }) => {
        this.handleStoreSaveSuccess(completionResponse, siteUrl, trimmedSubdomain);
      },
      error: (error: any) => {
        this.connMessageService.showToastMessage(
          error?.error?.message || error?.message || 'Error creating your store site!',
          'error'
        );
      }
    });
  }

  /** AI-editor create-site path: goes through the same auth-svc gateway
   *  (businessSetupService.createB2CSite → online-store/create-site) as the WordPress path below,
   *  rather than calling editor.lincsell.com's b2c-create-site directly from the browser — keeps
   *  the bearer token / x-bussiness-apikey server-side instead of visible in the browser's Network
   *  tab. No separate "theme" save step (the AI editor provisions its own theme). templateId is the
   *  selected catalog entry's real id (aiEditorStoreTemplates[].key, sourced from GET
   *  online-store/templates' template.id — e.g. "modern-boutique-a1b2c3"), not the WordPress
   *  picker's numeric templateID. NOTE: per the CreateOnlineStoreSite source you shared, this
   *  gateway currently has its call to the real site builder stubbed out ("External site builder
   *  call removed") — it fabricates a success response from the domain string alone, so no real
   *  AI-editor site is actually provisioned yet. That's a backend gap, not fixable from here; this
   *  path will start creating real sites automatically once that stub is restored. */
  private createAiEditorStore(username: string, userId: string, businessId: number | string, trimmedSubdomain: string): void {
    this.isSavingStore = true;
    const brandColorNames = this.selectedBrandColors
      .map((code) => this.getBrandColorOption(code)?.name)
      .filter((name): name is string => !!name);
    const template = this.selectedTemplate;
    const templateId = template?.key || 'classic';
    // First selected brand color is the primary; template's catalog color is only a fallback.
    const primaryColor = this.getBrandColorOption(this.selectedBrandColors[0])?.hex || template?.primaryColor;

    this.businessSetupService.createB2CSite(
      userId,
      businessId,
      username,
      trimmedSubdomain,
      templateId,
      brandColorNames,
      {
        templateLabel: template?.name,
        templateThumbnailUrl: template?.image,
        templateCategory: template?.category,
        templateDemoUrl: template?.demoUrl,
        templatePrimaryColor: primaryColor,
        templatePreviewImages: template?.previewImages
      }
    ).pipe(
      switchMap((editorResponse: any) => {
        const siteUrl = this.resolveCreatedStoreUrl(editorResponse);
        if (editorResponse?.success !== true || !siteUrl) {
          return throwError(() => new Error(editorResponse?.message || 'Error creating your store site!'));
        }

        const completionPayload: OnlineStoreCreatedPayload = {
          userId,
          businessId,
          onlineStoreUrl: siteUrl,
          onlineStoreName: this.toStoreName(trimmedSubdomain),
          onlineStoreTemplateId: templateId,
          onlineStoreTemplateLabel: template?.name,
          onlineStoreTemplateThumbnailUrl: template?.image,
          onlineStoreTemplateCategory: template?.category,
          onlineStoreTemplateDemoUrl: template?.demoUrl,
          onlineStoreTemplatePrimaryColor: primaryColor,
          onlineStoreTemplatePreviewImages: template?.previewImages,
          brandColors: brandColorNames
        };
        const siteId = editorResponse?.data?.siteId ?? editorResponse?.data?.id;
        if (siteId) {
          completionPayload.webStoreId = siteId;
        }

        return this.businessSetupService.markOnlineStoreCreated(completionPayload).pipe(
          map((completionResponse: any) => ({ completionResponse, siteUrl }))
        );
      }),
      map(({ completionResponse, siteUrl }) => {
        if (completionResponse?.success !== true) {
          throw new Error(completionResponse?.message || 'Unable to finish online store setup.');
        }
        return { completionResponse, siteUrl };
      }),
      finalize(() => {
        this.isSavingStore = false;
      })
    ).subscribe({
      next: ({ completionResponse, siteUrl }) => {
        this.handleStoreSaveSuccess(completionResponse, siteUrl, trimmedSubdomain);
      },
      error: (error: any) => {
        this.connMessageService.showToastMessage(
          error?.error?.message || error?.message || 'Error creating your store site!',
          'error'
        );
      }
    });
  }

  private handleStoreSaveSuccess(completionResponse: any, siteUrl: string, trimmedSubdomain: string): void {
    this.storeJustCreated = true;
    this.patchSavedStore(siteUrl, trimmedSubdomain, completionResponse?.data);
    this.userService.getWebStoreConfiguration(true).subscribe({
      next: (webStoreRes: any) => {
        if (webStoreRes?.success) {
          GlobalService.webStoreConfiguration = webStoreRes.data;
        }
      }
    });

    // markOnlineStoreCreated's response doesn't carry the full businessSetup record back — refetch
    // via B2CGraphClientApi (GETUSERBYUSERNAME), same as requestCustomOnlineStore() above, so
    // GlobalService.userInfo picks up the server-persisted onlineStoreCreated/onlineStoreUrl/
    // onlineStoreTemplateId immediately instead of only showing correctly after a hard reload.
    const userEmail = GlobalService.userInfo?.email ?? GlobalService.userInfo?.username;
    if (userEmail) {
      this.userService.getUserByUserName(userEmail).pipe(catchError(() => of(null))).subscribe((userRes: any) => {
        if (userRes?.success && userRes?.data) {
          GlobalService.userInfo = userRes.data;
        }
      });
    }

    this.businessSetupProgressService.markDomainTemplateSaved();
    this.isTemplateDialogVisible = false;
    this.persistCreatedStoreTemplateKey();
    this.connMessageService.showToastMessage('Domain & store template saved successfully!', 'success');
  }

  private persistCreatedStoreTemplateKey(): void {
    if (!this.selectedTemplateKey) return;
    try {
      localStorage.setItem(this.createdStoreTemplateStorageKey, this.selectedTemplateKey);
    } catch {
      // The template card falls back to hidden when browser storage is unavailable.
    }
  }

  /** Resolves which catalog entry a saved/completed store's template id refers to. The id's own
   *  shape says which catalog it belongs to — WordPress templates use a small numeric templateID
   *  (legacy contract, 1-3 today); AI-editor templates don't have a stable numeric id
   *  (aiEditorStoreTemplates[].templateID is always the 0 placeholder — real identity is the
   *  string catalog id in .key), so those are matched by string key instead. Deliberately does
   *  NOT gate this on isAiEditorStore: that only reflects the account-wide active-editor setting
   *  for *new* signups today, not which catalog this particular store's template was originally
   *  saved against — a store created under the old WordPress flow keeps its numeric id even after
   *  the account-wide setting later switches to AI, so gating on isAiEditorStore searched only
   *  the (non-matching) AI catalog and left the selection permanently unresolved for such
   *  accounts. */
  private resolveSavedTemplateKey(setup: any): string {
    const rawTemplateId =
      setup?.onlineStoreTemplateId ?? setup?.OnlineStoreTemplateId
      ?? setup?.onlineStoreTemplateID ?? setup?.OnlineStoreTemplateID
      ?? setup?.templateID ?? setup?.TemplateID;

    const numericId = Number(rawTemplateId);
    const wordPressMatch = !Number.isNaN(numericId)
      ? this.wordPressStoreTemplates.find((template) => template.templateID === numericId)
      : undefined;
    if (wordPressMatch) return wordPressMatch.key;

    const key = String(rawTemplateId ?? '').trim();
    if (key && this.aiEditorStoreTemplates.some((template) => template.key === key)) return key;

    // The catalog fetch (GET online-store/templates) can miss this key — it hasn't resolved yet,
    // or the template was later unpublished/removed from the catalog — even though the account
    // genuinely has a saved AI template. The business profile itself now carries that template's
    // full display data directly (aiEditorTemplateId/Label/ThumbnailUrl/...), so fall back to
    // reconstructing the entry from there instead of losing the selection.
    const profileTemplate = this.resolveSavedAiTemplateFromProfile(setup);
    if (profileTemplate) {
      if (!this.aiEditorStoreTemplates.some((template) => template.key === profileTemplate.key)) {
        this.aiEditorStoreTemplates = [...this.aiEditorStoreTemplates, profileTemplate];
      }
      return profileTemplate.key;
    }

    return '';
  }

  /** Builds a StoreTemplateOption straight from the business profile's own aiEditorTemplate*
   *  fields (set by the backend when the AI template is saved), without depending on that key
   *  still existing in the separately-fetched online-store/templates catalog. */
  private resolveSavedAiTemplateFromProfile(setup: any): StoreTemplateOption | undefined {
    const key = String(setup?.aiEditorTemplateId ?? setup?.AiEditorTemplateId ?? '').trim();
    if (!key) return undefined;

    const thumbnailUrl = setup?.aiEditorTemplateThumbnailUrl ?? setup?.AiEditorTemplateThumbnailUrl;
    const previewImages = setup?.aiEditorTemplatePreviewImages ?? setup?.AiEditorTemplatePreviewImages;

    return {
      key,
      name: String(setup?.aiEditorTemplateLabel ?? setup?.AiEditorTemplateLabel ?? key),
      description: '',
      image: thumbnailUrl ? encodeURI(String(thumbnailUrl)) : 'assets/images/store-templates/tpl-classic.svg',
      templateID: 0,
      demoUrl: String(setup?.aiEditorTemplateDemoUrl ?? setup?.AiEditorTemplateDemoUrl ?? ''),
      category: String(setup?.aiEditorTemplateCategory ?? setup?.AiEditorTemplateCategory ?? ''),
      primaryColor: String(setup?.aiEditorTemplatePrimaryColor ?? setup?.AiEditorTemplatePrimaryColor ?? ''),
      previewImages: Array.isArray(previewImages) ? previewImages.map(String) : []
    };
  }

  /** Same both-catalogs reasoning as resolveSavedTemplateKey() above — the cached key was written
   *  by whichever catalog was active at store-creation time, which may no longer match today's
   *  isAiEditorStore-gated storeTemplates list. */
  private readCachedCreatedTemplateKey(): string {
    try {
      const cachedKey = localStorage.getItem(this.createdStoreTemplateStorageKey) || '';
      if (!cachedKey) return '';
      const matches = this.wordPressStoreTemplates.some((template) => template.key === cachedKey)
        || this.aiEditorStoreTemplates.some((template) => template.key === cachedKey);
      return matches ? cachedKey : '';
    } catch {
      return '';
    }
  }

  private get createdStoreTemplateStorageKey(): string {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const userId = GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId ?? 'user';
    const businessId = setup?.businessId ?? setup?.id ?? 'business';
    return `online-store-created-template-${userId}-${businessId}`;
  }

  private patchSavedStore(siteUrl: string, trimmedSubdomain: string, completedStore: any): void {
    this.subdomain = trimmedSubdomain;
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    if (!setup) return;

    setup.onlineStoreUrl = completedStore?.onlineStoreUrl ?? siteUrl;
    setup.onlineStoreCreated = completedStore?.onlineStoreCreated ?? true;
    setup.onlineStoreCreatedDate = completedStore?.onlineStoreCreatedDate ?? setup.onlineStoreCreatedDate ?? new Date().toISOString();
    setup.onlineStoreName = completedStore?.onlineStoreName ?? this.toStoreName(trimmedSubdomain);
    setup.onlineStoreTemplateId = completedStore?.onlineStoreTemplateId
      ?? (this.isAiEditorStore ? this.selectedTemplate?.key : this.selectedTemplate?.templateID);
    setup.brandColorSelections = [...this.selectedBrandColors];
  }

  private saveThemeConfiguration(trimmedSubdomain: string): Observable<any> {
    const selectedColorDetails = this.selectedBrandColors
      .map((code) => this.getBrandColorOption(code))
      .filter(Boolean);
    return this.businessSetupService.saveThemeInformation({
      templateID: this.selectedTemplate?.templateID,
      webStoreHost: `${trimmedSubdomain}.lincsell.com`,
      demoUrl: this.selectedTemplate?.demoUrl,
      imageUrl: this.selectedTemplate?.image,
      isDefault: true,
      brandColors: [...this.selectedBrandColors],
      brandColorDetails: selectedColorDetails
    });
  }

  private buildOnlineStoreCreatedPayload(
    userId: string,
    businessId: number | string,
    subdomain: string,
    siteUrl: string,
    editorResponse: any
  ): OnlineStoreCreatedPayload {
    const payload: OnlineStoreCreatedPayload = {
      userId,
      businessId,
      onlineStoreUrl: siteUrl,
      onlineStoreName: this.toStoreName(subdomain),
      onlineStoreTemplateId: this.selectedTemplate?.templateID,
      brandColors: this.selectedBrandColors
        .map((code) => this.getBrandColorOption(code)?.name)
        .filter((name): name is string => !!name),
      demoUrl: this.selectedTemplate?.demoUrl,
      imageUrl: this.selectedTemplate?.image
    };
    const webStoreId = editorResponse?.webStoreId ?? editorResponse?.data?.webStoreId;
    if (webStoreId) {
      payload.webStoreId = webStoreId;
    }
    return payload;
  }

  private resolveCreatedStoreUrl(response: any): string {
    return String(
      response?.onlineStoreUrl
      ?? response?.url
      ?? response?.data?.onlineStoreUrl
      ?? response?.data?.url
      ?? ''
    ).trim();
  }

  private toStoreName(subdomain: string): string {
    return subdomain ? subdomain.charAt(0).toUpperCase() + subdomain.slice(1) : subdomain;
  }

  /** WordPress store URLs are "<subdomain>.lincsell.com" — the subdomain IS the hostname.
   *  AI-editor store URLs are all on one fixed host ("editor.lincsell.com/storefront/<subdomain>")
   *  — the merchant's actual subdomain is the last path segment instead, so that must be checked
   *  first or every AI-editor account resolves to the literal host name "editor". */
  private resolveSavedSubdomain(storeUrl: string): string {
    if (!storeUrl) return '';

    try {
      const normalizedUrl = /^https?:\/\//i.test(storeUrl) ? storeUrl : `https://${storeUrl}`;
      const url = new URL(normalizedUrl);
      const pathSegments = url.pathname.split('/').filter(Boolean);
      if (pathSegments.length > 0) {
        return pathSegments[pathSegments.length - 1].toLowerCase();
      }
      const hostname = url.hostname.toLowerCase();
      return hostname.endsWith('.lincsell.com') ? hostname.slice(0, -'.lincsell.com'.length) : hostname.split('.')[0];
    } catch {
      const withoutProtocol = storeUrl.replace(/^https?:\/\//i, '');
      const [hostPart, ...pathParts] = withoutProtocol.split('/').filter(Boolean);
      const lastSegment = pathParts[pathParts.length - 1];
      return (lastSegment || hostPart || '').replace(/\.lincsell\.com$/i, '').toLowerCase();
    }
  }

  private persistCustomStoreRequestDetails(): void {
    try {
      localStorage.setItem(this.customStoreRequestStorageKey, JSON.stringify({
        subdomain: this.subdomain.trim(),
        selectedTemplateKey: this.selectedTemplateKey,
        selectedBrandColors: [...this.selectedBrandColors]
      }));
    } catch {
      // The API request remains valid when browser storage is unavailable.
    }
  }

  private restoreCustomStoreRequestDetails(): void {
    try {
      const saved = JSON.parse(localStorage.getItem(this.customStoreRequestStorageKey) || '{}');
      this.subdomain = validateSubdomainFormat(String(saved?.subdomain || this.subdomain));
      const savedTemplateKey = String(saved?.selectedTemplateKey || '');
      if (this.storeTemplates.some((template) => template.key === savedTemplateKey)) {
        this.selectedTemplateKey = savedTemplateKey;
      }
      if (Array.isArray(saved?.selectedBrandColors)) {
        this.selectedBrandColors = saved.selectedBrandColors
          .map((color: any) => String(color))
          .filter((color: string) => !!this.getBrandColorOption(color));
      }
    } catch {
      // Ignore malformed or unavailable local state.
    }
  }

  private get customStoreRequestStorageKey(): string {
    const setup = GlobalService.userInfo?.businessSetup?.[0];
    const userId = GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId ?? 'user';
    const businessId = setup?.businessId ?? setup?.id ?? 'business';
    return `online-store-custom-request-${userId}-${businessId}`;
  }
}
